import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProductionLayoutWrapper from "../../components/ProductionLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaPlus,
  FaChevronDown,
  FaChevronUp,
  FaTrash,
  FaEdit,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const toDDMMYYYY = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const ProductionBOMListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ Safe localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 8), [branchIdLS]);
  const branch = useMemo(() => String(branchTypeLS || "").trim(), [branchTypeLS]);
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-branchtype": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [showFilters, setShowFilters] = useState(true);

  const [formData, setFormData] = useState({
    fromDate: toDDMMYYYY(new Date()),
    toDate: toDDMMYYYY(new Date()),
    projectid: 0,
    productionid: 0,
  });

  const [projects, setProjects] = useState([]);
  const [productions, setProductions] = useState([]);
  const [bomData, setBomData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // ✅ selection
  const [selectedIds, setSelectedIds] = useState([]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const loadProjects = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/bomlisting/projects`, {
        params: { branchId, userId },
        headers: commonHeaders,
      });
      setProjects(res.data?.data || []);
    } catch (err) {
      console.error("Projects load error:", err);
      toast.error(err?.response?.data?.message || "Failed to load projects");
      setProjects([]);
    }
  };

  const loadProductions = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/bomlisting/productions`, {
        params: { branchId, userId },
        headers: commonHeaders,
      });
      setProductions(res.data?.data || []);
    } catch (err) {
      console.error("Productions load error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to load production names"
      );
      setProductions([]);
    }
  };

  useEffect(() => {
    loadProjects();
    loadProductions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, userId]);

  const handleSearch = async () => {
    if (!branchId) {
      toast.error("BranchId missing!");
      return;
    }

    if (!formData.fromDate || !formData.toDate) {
      toast.warn("Please select From Date and To Date");
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const res = await axios.get(`${BASE_URL}/api/bomlisting/search`, {
        params: {
          branchId,
          userId,
          fromDate: formData.fromDate,
          toDate: formData.toDate,
          projectid: Number(formData.projectid || 0),
          productionid: Number(formData.productionid || 0),
        },
        headers: commonHeaders,
      });

      const rows = res.data?.data || [];
      setBomData(rows);
      setSelectedIds([]);

      if (rows.length === 0) toast.info("No Records found");
    } catch (err) {
      console.error("Search error:", err);
      toast.error(err?.response?.data?.message || "Search failed");
      setBomData([]);
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    navigate("/TarMixing/Tar_BOM_Entry");
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const allSelected =
    bomData.length > 0 && selectedIds.length === bomData.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(bomData.map((r) => Number(r.bomId)));
    }
  };

  const handleUpdate = () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select one record to update.");
      return;
    }

    if (selectedIds.length > 1) {
      toast.warn("Please select only one record for update.");
      return;
    }

    const id = selectedIds[0];
    const rowData = bomData.find((r) => Number(r.bomId) === Number(id));

    if (!rowData) {
      toast.error("Selected record not found!");
      return;
    }

    navigate("/TarMixing/Tar_BOM_Entry", {
      state: {
        editMode: true,
        bomId: id,
        rowData,
      },
    });
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one record to delete.");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      const res = await axios.delete(
        `${BASE_URL}/api/bomlisting/deleteSelected`,
        {
          params: {
            branchid: branchId,
            loginuserid: userId,
          },
          data: { bomids: selectedIds },
          headers: commonHeaders,
        }
      );

      if (res.data?.success === false) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      toast.success("Deleted successfully!");
      setBomData((prev) =>
        prev.filter((r) => !selectedIds.includes(Number(r.bomId)))
      );
      setSelectedIds([]);
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  return (
    <ProductionLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PRODUCTION BOM LISTING</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Production BOM Listing
              </h2>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={handleUpdate}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                <FaEdit />
                Update
              </button>

              <button
                onClick={handleDelete}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-red-600 hover:bg-red-700 text-white"
                }`}
              >
                <FaTrash />
                Delete
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`p-4 rounded-xl border mb-6 ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="text"
                    value={formData.fromDate}
                    onChange={(e) => handleInputChange("fromDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="text"
                    value={formData.toDate}
                    onChange={(e) => handleInputChange("toDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Project
                  </label>
                  <select
                    value={formData.projectid}
                    onChange={(e) => handleInputChange("projectid", Number(e.target.value))}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value={0}>All Projects</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Production Name
                  </label>
                  <select
                    value={formData.productionid}
                    onChange={(e) => handleInputChange("productionid", Number(e.target.value))}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value={0}>All Productions</option>
                    {productions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center items-center gap-6 mt-6">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  } ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          )}

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : bomData.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className={`min-w-full text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleSelectAll}
                          />
                          Select
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">S.No</th>
                      <th className="px-4 py-3 text-left font-semibold">Entry Date</th>
                      <th className="px-4 py-3 text-left font-semibold">Project</th>
                      <th className="px-4 py-3 text-left font-semibold">Production Name</th>
                      <th className="px-4 py-3 text-left font-semibold">Production Code</th>
                      <th className="px-4 py-3 text-right font-semibold">Qty</th>
                      <th className="px-4 py-3 text-left font-semibold">UOM</th>
                    </tr>
                  </thead>

                  <tbody className={`${isDark ? "bg-gray-900" : "bg-white"}`}>
                    {bomData.map((row, idx) => {
                      const id = Number(row.bomId);
                      const checked = selectedIds.includes(id);

                      return (
                        <tr
                          key={id || idx}
                          className={`border-t ${
                            isDark
                              ? "border-gray-800 hover:bg-gray-800/50"
                              : "border-gray-100 hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRow(id)}
                            />
                          </td>
                          <td className="px-4 py-3">{idx + 1}</td>
                          <td className="px-4 py-3">{row.entryDate || "-"}</td>
                          <td className="px-4 py-3">{row.projectName || "-"}</td>
                          <td className="px-4 py-3">{row.productionName || "-"}</td>
                          <td className="px-4 py-3">{row.productionCode || "-"}</td>
                          <td className="px-4 py-3 text-right">{row.productionQty ?? 0}</td>
                          <td className="px-4 py-3">{row.tarUOM || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className={`px-4 py-3 text-sm ${isDark ? "bg-gray-800 text-gray-300" : "bg-gray-50 text-gray-700"}`}>
                  Selected: <span className="font-semibold">{selectedIds.length}</span>
                </div>
              </div>
            )}
          </div>

          <ToastContainer />
        </div>
      </div>
    </ProductionLayoutWrapper>
  );
};

export default ProductionBOMListing;